import React, { useEffect, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

import { Text } from '@wartek-id/text'
import { Spinner } from '@wartek-id/spinner'

import { useWindowDimensions } from 'utils/hooks/useWindowDimensions'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'

// for dynamic import
// const workerSrc = async () => await import('utils/pdfWorkerEntry')

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const INITIAL_SCALE = 1
const PADDING_SIZE = 0

export const PDFPreviewer = ({ data, isRemoteUrl = false }) => {
  const [wrapperWidth, setWrapperWidth] = useState<number>(0)
  const [wrapperHeight, setWrapperHeight] = useState<number>(0)
  const { width, height, getElementDimensionById } = useWindowDimensions()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const file = useMemo(() => ({ data }), [data])
  const rootRef = React.useRef<HTMLDivElement>(null)

  const getFileToLoad = () => {
    if (isRemoteUrl) {
      return {
        // TODO: check cors if API already ready with true data
        url: file.data,
      }
    }
    return file
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setIsLoaded(true)
    setNumPages(numPages)
  }

  useEffect(() => {
    const { width: elementWidth, height: elementHeight } =
      getElementDimensionById('viewerWrapper')
    setWrapperWidth(elementWidth)
    setWrapperHeight(elementHeight)
  }, [getElementDimensionById])

  return (
    <>
      <div
        ref={rootRef}
        className="relative border border-gray-5 rounded-sm bg-gray-5 w-full"
      >
        {isLoaded && <div className="z-10 absolute top-4" />}
        <div id="viewerContainer" className="relative z-0 overflow-auto">
          <div id="viewerWrapper">
            <Document
              file={getFileToLoad()}
              error={() => {
                return (
                  <div className="w-full h-full flex items-center justify-center">
                    <Text variant="body" color="critical">
                      <EmptyState
                        title="Terjadi Kesalahan dalam memuat dokumen"
                        fullscreen={false}
                        description="muat ulang halaman atau kembali ke halaman sebelumnya"
                      >
                        <EmptyStateAction
                          color="white"
                          onClick={() => history.back()}
                        >
                          Kembali Ke halaman sebelumnya
                        </EmptyStateAction>

                        <EmptyStateAction
                          color="black"
                          onClick={() => window.location.reload()}
                        >
                          Muat Ulang
                        </EmptyStateAction>
                      </EmptyState>
                    </Text>
                  </div>
                )
              }}
              loading={() => {
                const previewElement = (
                  <>
                    <Spinner size="lg" />
                    <Text as="span" className="pt-2" variant="body">
                      Harap Tunggu
                    </Text>
                  </>
                )
                return (
                  <Text
                    as="div"
                    className="w-full flex flex-col place-content-center place-items-center p-3 my-2 bg-surface-subdued border border-surface-neutral-subdued rounded"
                    variant="body"
                    color="informational"
                  >
                    {previewElement}
                  </Text>
                )
              }}
              externalLinkTarget="_blank"
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {[...Array(numPages)].map((_, index) => (
                <Page
                  key={`pdf-viewer-page-${index}@${INITIAL_SCALE}`}
                  pageNumber={index + 1}
                  className="flex place-content-center certificate-page"
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  devicePixelRatio={window.devicePixelRatio * 2}
                  scale={INITIAL_SCALE * 2}
                  width={(wrapperWidth || width) + -PADDING_SIZE}
                  height={(wrapperHeight || height) - PADDING_SIZE}
                />
              ))}
            </Document>
          </div>
        </div>
      </div>
    </>
  )
}

export default PDFPreviewer
